import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'

import { SegmentClickEventProps, trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { Tag } from '@infura/packages.ui/Tag'

import { linkClasses } from 'components/navigation/styles'
import useUser from 'hooks/useUser'
import { useEffect, useState } from 'react'

export const NavLink = ({
  customActiveClass,
  customPathMatcher,
  dataTestid,
  href,
  icon,
  label,
  trackClickEvent
}: {
  dataTestid?: string
  icon: IconDefinition
  label: string
  href: string
  trackClickEvent?: SegmentClickEventProps
  exact?: boolean
  customActiveClass?: string
  customPathMatcher?: (path: string) => boolean
}) => {
  const activePath = usePathname()
  const isActive = customPathMatcher ? customPathMatcher(activePath) : activePath?.startsWith(href)
  return (
    <Link
      className={clsx(
        linkClasses,
        'group',
        isActive ? 'text-text-default' : 'text-text-alternative  hover:text-text-default'
      )}
      data-testid={dataTestid}
      href={href}
      onClick={() => {
        if (trackClickEvent) {
          trackClickForSegmentAnalytics(trackClickEvent)
        }
      }}
    >
      <FontAwesomeIcon
        className={`${
          isActive
            ? customActiveClass
              ? customActiveClass
              : 'text-primary-default'
            : 'text-text-alternative group-hover:text-text-default'
        }`}
        icon={icon}
      />
      <span className='text-sm font-medium'>{label}</span>
    </Link>
  )
}

export const ExternalNavLink = ({
  dataTestid,
  href,
  icon,
  label,
  trackClickEvent
}: {
  dataTestid?: string
  icon: IconDefinition
  label: string
  href: string
  trackClickEvent?: SegmentClickEventProps
}) => {
  return (
    <a
      className={linkClasses}
      data-testid={dataTestid}
      href={href}
      onClick={() => {
        if (trackClickEvent) {
          trackClickForSegmentAnalytics(trackClickEvent)
        }
      }}
      rel='noopener noreferrer'
      target='_blank'
    >
      <FontAwesomeIcon icon={icon} />
      <span className='text-sm font-medium'>{label}</span>
    </a>
  )
}

const w3aLogo: IconDefinition = {
  // Any name you like
  icon: [
    16, // width
    14, // height
    [],
    '',

    'M8.1889 11.9938C8.15308 12.1256 8.2538 12.255 8.39218 12.255H9.60421C9.74259 12.255 9.84331 12.1256 9.80749 11.9938L9.20148 9.76393C9.14575 9.55888 8.85064 9.55888 8.79491 9.76393L8.1889 11.9938Z M6.98205 13.421C6.88924 13.7625 6.57534 14 6.21673 14H4.57621C4.05525 14 3.67606 13.5128 3.8109 13.0167L7.17635 0.633352C7.27786 0.259824 7.62119 8.90099e-05 8.01341 8.90099e-05H9.96584C10.3581 8.90099e-05 10.7014 0.259824 10.8029 0.633352L14.1684 13.0167C14.3032 13.5128 13.924 14 13.403 14H11.7625C11.4039 14 11.09 13.7625 10.9972 13.421L9.36032 7.39807C9.2587 7.02415 8.72055 7.02415 8.61892 7.39807L6.98205 13.421Z M15.234 11.0875C15.1291 11.4735 14.5736 11.4735 14.4687 11.0875L13.2469 6.59181C13.2233 6.50489 13.2233 6.41337 13.2469 6.32645L14.804 0.597076C14.8997 0.244892 15.2234 0 15.5932 0H17.1825C17.7197 0 18.1108 0.502434 17.9717 1.01407L15.234 11.0875ZM4.75106 6.58431C4.77468 6.49739 4.77468 6.40587 4.75106 6.31895L3.19601 0.597076C3.10029 0.244892 2.77659 0 2.40678 0H0.817521C0.280274 0 -0.110759 0.502434 0.0282907 1.01407L2.76394 11.08C2.86884 11.466 3.42435 11.466 3.52925 11.08L4.75106 6.58431Z'
  ],

  // @ts-expect-error - types don't include iconName
  iconName: 'iconWeb3Auth',
  // @ts-expect-error - types don't include prefix
  prefix: 'icon'
}

export const Web3AuthLink = () => {
  const { user } = useUser()
  const isSocialLoginUser = !!user?.login_provider
  const [href, setHref] = useState<string>('')

  useEffect(() => {
    const newHref = isSocialLoginUser ? getSocialLoginUrl() : '/api/oauth/auth-login'
    setHref(newHref)
  }, [isSocialLoginUser])

  if (!href) return null

  return (
    <a
      className='hover:bg-background-default-hover group flex items-center justify-between rounded-lg pr-4 no-underline transition-colors duration-200'
      data-testid='web3-auth-link'
      href={href}
      rel='noopener noreferrer'
      target='_blank'
    >
      <span
        className={clsx(linkClasses, 'group-hover:text-text-default group-hover:underline')}
        onClick={() => {
          trackClickForSegmentAnalytics({
            clickText: 'web3auth',
            location: ' navbar',
            type: 'Text'
          })
        }}
      >
        <FontAwesomeIcon className='group-hover:text-[#0364FF]' icon={w3aLogo} />
        <span className='flex justify-between text-sm font-medium'>web3auth</span>
      </span>
      <Tag theme='warning'>New</Tag>
    </a>
  )
}

const web3LoginUrl = process.env.NEXT_PUBLIC_WEB_3_LOGIN_URL

if (!web3LoginUrl) {
  throw new Error('NEXT_PUBLIC_WEB_3_LOGIN_URL is not set')
}

function getSocialLoginUrl() {
  const w3aSessionStore = localStorage.getItem('auth_store') ?? ''
  const hashParams = new URLSearchParams()
  const b64Value = btoa(w3aSessionStore)
  hashParams.append('b64Params', b64Value)
  const dashboardUrl = new URL(web3LoginUrl ?? '')
  dashboardUrl.pathname = 'end'
  dashboardUrl.hash = `#${hashParams.toString()}`
  return dashboardUrl.href
}
